import { useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { SxProps, Theme } from "@mui/material/styles";
import toast from "components/partials/toast/toast";
import { Box } from "ui/box";
import { Checkbox } from "ui/checkbox";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import { Select } from "ui/select";
import { Stack } from "ui/stack";
import { TextArea } from "ui/text-area";
import { TouchpointType } from "types/touchpoint";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { touchpointKeys, touchpointVersionKeys } from "hooks/api/query-keys";
import { useQueueCreativesRewrite } from "hooks/use-queue-creatives-rewrite";
import { getTouchpointVersion } from "generated/api/touchpoint-version";
import type { RewriteTouchpointContentsRequest } from "generated/schemas";
import { formatFiscalYear, getActionTouchpointTypeLabel, getErrorMessage, getFYOptions } from "utilities";
import { RollToNextFYSelectedItems } from "../../components/roll-to-next-fy-selected-items/roll-to-next-fy-selected-items";

const touchpointVersionApi = getTouchpointVersion();

/**
 * The backend caps `emailPrompt` at 4000 characters (`RewriteTouchpointContentsRequest`), and
 * `buildRollToNextFYRewritePrompt` prepends up to ~250 characters of roll instructions.
 */
const ADDITIONAL_INSTRUCTIONS_MAX_LENGTH = 3500;

const CONTENT_SX: SxProps<Theme> = { gap: 3, width: "100%" };

const FY_SELECT_SX: SxProps<Theme> = { width: 128 };

interface UseRollToNextFYModalProps {
  onSuccess?: () => void;
}

export const buildRollToNextFYDescription = (
  sourceFiscalYear: number | undefined,
  targetFiscalYear: number,
  additionalInstructions?: string,
): string => {
  const targetLabel = formatFiscalYear(targetFiscalYear);
  const sourceLabel = formatFiscalYear(sourceFiscalYear);
  const rolledLabel = sourceLabel
    ? `Rolled from ${sourceLabel} to ${targetLabel}`
    : `Rolled to ${targetLabel}`;
  const instructions = additionalInstructions?.trim();

  return instructions ? `${rolledLabel}. ${instructions}` : rolledLabel;
};

/**
 * The Rewriter has no notion of fiscal year — it only sees the creative content and this prompt —
 * so the source and target years have to be spelled out for it to update year-specific references.
 */
export const buildRollToNextFYRewritePrompt = (
  sourceFiscalYear: number | undefined,
  targetFiscalYear: number,
  additionalInstructions?: string,
): string => {
  const targetLabel = formatFiscalYear(targetFiscalYear);
  const sourceLabel = formatFiscalYear(sourceFiscalYear);
  const yearShift = sourceFiscalYear ? targetFiscalYear - sourceFiscalYear : 0;
  const rollInstruction = sourceLabel
    ? `This creative is being rolled from fiscal year ${sourceLabel} to ${targetLabel}. ` +
      `Update every year-specific reference and date in the content so it reflects ${targetLabel}: ` +
      `shift calendar years by ${yearShift} and leave no references to ${sourceLabel}.`
    : `This creative is being rolled to fiscal year ${targetLabel}. ` +
      `Update every year-specific reference and date in the content so it reflects ${targetLabel}.`;
  const instructions = additionalInstructions?.trim();

  return instructions ? `${rollInstruction}\n${instructions}` : rollInstruction;
};

const buildRewriteRequests = (
  items: TouchpointAttributes[],
  targetFiscalYear: number,
  additionalInstructions: string,
): RewriteTouchpointContentsRequest[] => {
  const emailIdsBySourceFiscalYear = new Map<number | undefined, string[]>();

  items
    .filter((item) => item.type === TouchpointType.EMAIL)
    .forEach((item) => {
      const sourceFiscalYear = item.latestVersion?.fiscalYear;
      emailIdsBySourceFiscalYear.set(sourceFiscalYear, [
        ...(emailIdsBySourceFiscalYear.get(sourceFiscalYear) ?? []),
        item.id,
      ]);
    });

  return Array.from(emailIdsBySourceFiscalYear, ([sourceFiscalYear, touchpointIds]) => ({
    touchpointIds,
    emailPrompt: buildRollToNextFYRewritePrompt(sourceFiscalYear, targetFiscalYear, additionalInstructions),
  }));
};

export const useRollToNextFYModal = ({ onSuccess }: UseRollToNextFYModalProps = {}) => {
  const queryClient = useQueryClient();
  const { queueRewrites } = useQueueCreativesRewrite();
  const fyOptions = getFYOptions();
  const [itemsToRoll, setItemsToRoll] = useState<TouchpointAttributes[]>([]);
  const [selectedFY, setSelectedFY] = useState(fyOptions[fyOptions.length - 1]);
  const [rewriteEmails, setRewriteEmails] = useState(false);
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const singleItem = itemsToRoll.length === 1 ? itemsToRoll[0] : undefined;
  const singleItemTypeLabel = singleItem ? getActionTouchpointTypeLabel(singleItem.type) : undefined;
  const emailCount = itemsToRoll.filter((item) => item.type === TouchpointType.EMAIL).length;
  const shouldRewrite = emailCount > 0 && rewriteEmails;
  const rollInstructions = shouldRewrite ? additionalInstructions : undefined;

  const openRollToNextFYModal = useCallback(
    (items: TouchpointAttributes | TouchpointAttributes[]) => {
      const itemsArray = Array.isArray(items) ? items : [items];
      setItemsToRoll(itemsArray);
    },
    [],
  );

  const closeRollToNextFYModal = useCallback(() => {
    setItemsToRoll([]);
    setRewriteEmails(false);
    setAdditionalInstructions("");
  }, []);

  const rewriteRolledEmails = useCallback(
    async (rolledItems: TouchpointAttributes[], fiscalYear: number) => {
      const rewriteRequests = buildRewriteRequests(rolledItems, fiscalYear, additionalInstructions);
      if (rewriteRequests.length === 0) return;

      // Roll is only offered on a campaign's creatives page, so every selected item shares one campaign.
      const { clientId, campaignId } = rolledItems[0];

      try {
        const { queued, failedRequestCount } = await queueRewrites({
          clientId,
          campaignId,
          requests: rewriteRequests,
        });

        if (queued.length > 0) {
          toast.info({
            data: {
              title: "Writing in progress",
              message: "Written content will appear in preview, and notification will be sent upon completion.",
            },
            autoClose: false,
          });
        }

        if (failedRequestCount > 0) {
          toast.error({
            data: {
              title: "Failed to start rewriting for the new fiscal year",
              message: "Some rolled emails keep their previous fiscal year content. Please rewrite them manually.",
            },
          });
        } else if (queued.length === 0) {
          toast.warning({
            data: {
              title: "No emails were rewritten",
              message: "Emails that are already being written can't be rewritten for the new fiscal year.",
            },
          });
        }
      } catch (error) {
        toast.error({
          data: {
            title: "Failed to start rewriting for the new fiscal year",
            message: getErrorMessage(error) || "Please try again.",
          },
        });
      }
    },
    [additionalInstructions, queueRewrites],
  );

  const handleRollToNextFY = useCallback(async () => {
    if (itemsToRoll.length === 0) return;

    const fiscalYear = selectedFY.value;
    const showRollFailedToast = () =>
      toast.error({
        data: {
          title: `Failed to roll ${singleItemTypeLabel?.toLowerCase() ?? "creatives"} to next FY`,
        },
      });

    try {
      setIsLoading(true);
      const settled = await Promise.allSettled(
        itemsToRoll.map((item) =>
          touchpointVersionApi.createNewDraftVersion(
            {
              clientId: item.clientId,
              campaignId: item.campaignId,
              touchpointId: item.id,
              id: item.latestVersion!.id,
            },
            {
              description: buildRollToNextFYDescription(
                item.latestVersion?.fiscalYear,
                fiscalYear,
                rollInstructions,
              ),
              applyClientTheme: item.latestVersion?.applyClientTheme ?? false,
              fiscalYear,
            },
          ),
        ),
      );
      const rolledItems = itemsToRoll.filter((_, index) => settled[index].status === "fulfilled");
      const failedCount = itemsToRoll.length - rolledItems.length;

      rolledItems.forEach((item) => {
        queryClient.invalidateQueries({
          queryKey: touchpointVersionKeys.listScope(item.clientId, item.campaignId, item.id),
        });
        queryClient.invalidateQueries({
          queryKey: touchpointKeys.detail(item.clientId, item.campaignId, item.id),
        });
      });
      queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });

      if (rolledItems.length === 0) {
        showRollFailedToast();
        return;
      }

      if (failedCount > 0) {
        toast.error({
          data: {
            title: `Failed to roll ${failedCount} of ${itemsToRoll.length} creatives to next FY`,
          },
        });
      }

      toast.success({
        data: singleItemTypeLabel
          ? {
              title: `${singleItemTypeLabel} rolled`,
              message: "New draft created",
            }
          : {
              title: "Creatives rolled",
              message: "New drafts created",
            },
      });

      if (shouldRewrite) {
        await rewriteRolledEmails(rolledItems, fiscalYear);
      }

      onSuccess?.();
    } catch {
      showRollFailedToast();
    } finally {
      setIsLoading(false);
      closeRollToNextFYModal();
    }
  }, [
    itemsToRoll,
    selectedFY,
    rollInstructions,
    shouldRewrite,
    singleItemTypeLabel,
    rewriteRolledEmails,
    onSuccess,
    closeRollToNextFYModal,
  ]);

  const rollToNextFYModalMarkup = useMemo(() => {
    const isOpen = itemsToRoll.length > 0;
    if (!isOpen) return null;

    return (
      <ConfirmationDialog
        open
        onClose={closeRollToNextFYModal}
        onConfirm={handleRollToNextFY}
        headline={`Roll ${singleItemTypeLabel?.toLowerCase() ?? "creatives"} to next FY`}
        confirmButtonText="Submit"
        confirming={isLoading}>
        <Stack sx={CONTENT_SX}>
          <Box sx={FY_SELECT_SX}>
            <Select
              selectName="fy-select"
              disableClearable
              ariaLabel="Select Fiscal Year"
              options={fyOptions}
              value={selectedFY}
              onChange={(val) => val && setSelectedFY(val)}
            />
          </Box>
          {emailCount > 0 && (
            <Checkbox
              name="roll-to-next-fy-rewrite"
              label={`Rewrite ${emailCount === 1 ? "email" : "emails"} for next FY`}
              size="sm"
              checked={rewriteEmails}
              onChange={setRewriteEmails}
            />
          )}
          {shouldRewrite && (
            <TextArea
              name="roll-to-next-fy-instructions"
              label="Additional instructions"
              helperText="Include any instructions if changes are needed for new fiscal year."
              value={additionalInstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
              resize="vertical"
              slotProps={{ input: { maxLength: ADDITIONAL_INSTRUCTIONS_MAX_LENGTH } }}
            />
          )}
          {!singleItem && <RollToNextFYSelectedItems items={itemsToRoll} />}
        </Stack>
      </ConfirmationDialog>
    );
  }, [
    itemsToRoll,
    selectedFY,
    fyOptions,
    singleItem,
    singleItemTypeLabel,
    emailCount,
    rewriteEmails,
    shouldRewrite,
    additionalInstructions,
    isLoading,
    closeRollToNextFYModal,
    handleRollToNextFY,
  ]);

  return {
    openRollToNextFYModal,
    rollToNextFYModalMarkup,
  };
};
